import { useMemo, useState } from 'react';
import type { ClientConfig } from '@ferro/config';
import {
  ApiError,
  commitTenant,
  readSession,
  type TenantCommitErrorBody,
  type TenantCommitResponse,
} from '../../../lib/api.js';
import {
  assembleProvisioningSpec,
  type AssemblyIssue,
  type ProvisioningSpec,
} from '../../../lib/tenant-kit-assembler.js';
import {
  TENANT_WIZARD_STEP_LABELS,
  type TenantWizardStep,
} from '../../../lib/tenant-draft.js';

/**
 * Step 7 of the tenant provisioning wizard (D1.5.8) — Review + commit.
 *
 * Responsibilities:
 *   1. Summarize the assembled draft payload for the platform admin.
 *   2. Surface any pre-commit issues (missing slots, malformed fields)
 *      with chip links back to the owning step.
 *   3. On commit-click, POST the spec to `/api/platform/tenants/commit`
 *      (D1.5.8 server route) and render the success/failure state.
 *
 * Happy path: Tenant + Config + Branding + Modules + Owner + Domains +
 * Audit rows written in one transaction by the D1.6.1 shared commit
 * service. DB failure rolls the whole thing back — no partial state.
 */

type StepReviewCommitState =
  | { kind: 'idle' }
  | { kind: 'submitting' }
  | { kind: 'created'; response: Extract<TenantCommitResponse, { kind: 'created' }> }
  | { kind: 'already_exists'; response: Extract<TenantCommitResponse, { kind: 'already_exists' }> }
  | { kind: 'error'; status: number; message: string; body: TenantCommitErrorBody | null };

type CommitClient = (
  apiUrl: string,
  token: string,
  spec: unknown,
) => Promise<TenantCommitResponse>;

const defaultCommitClient: CommitClient = (apiUrl, token, spec) =>
  commitTenant(apiUrl, token, spec);

type StepReviewProps = {
  payload: Record<string, unknown>;
  config: ClientConfig;
  /** Test seam — overrides the network POST path. */
  commit?: CommitClient;
  /** Callback when the admin jumps back to an earlier step via a fix chip. */
  onGoToStep?: (step: TenantWizardStep) => void;
};

export function StepReview({
  payload,
  config,
  commit = defaultCommitClient,
  onGoToStep,
}: StepReviewProps): JSX.Element {
  const assembly = useMemo(() => assembleProvisioningSpec(payload), [payload]);
  const [state, setState] = useState<StepReviewCommitState>({ kind: 'idle' });

  const onCommit = async (): Promise<void> => {
    if (!assembly.ok) return;
    const session = readSession();
    if (!session.token) {
      setState({
        kind: 'error',
        status: 401,
        message: 'Sign in again to commit.',
        body: null,
      });
      return;
    }
    setState({ kind: 'submitting' });
    try {
      const res = await commit(config.deployment.apiUrl, session.token, assembly.spec);
      if (res.kind === 'created') {
        setState({ kind: 'created', response: res });
      } else {
        setState({ kind: 'already_exists', response: res });
      }
    } catch (e) {
      if (e instanceof ApiError) {
        const rawBody = (e as ApiError & { body?: TenantCommitErrorBody | null }).body;
        setState({
          kind: 'error',
          status: e.status,
          message: e.message,
          body: rawBody ?? null,
        });
      } else {
        setState({
          kind: 'error',
          status: 0,
          message: e instanceof Error ? e.message : 'Network error',
          body: null,
        });
      }
    }
  };

  return (
    <div className="ferro-form" aria-label="Review and commit — Step 7">
      <p className="ferro-muted">
        Committing will write the tenant record, configuration, branding, module
        activations, domains, and first owner in a single transaction. DB failures
        roll back cleanly — no partial state.
      </p>

      {assembly.ok
        ? renderSummary(assembly.spec)
        : renderIssues(assembly.issues, onGoToStep)}

      {renderCommitState(state)}

      <div className="ferro-wizard__nav" style={{ marginTop: '1.5rem' }}>
        <button
          type="button"
          className="ferro-btn ferro-btn--primary"
          disabled={!assembly.ok || state.kind === 'submitting' || state.kind === 'created'}
          onClick={() => void onCommit()}
          data-testid="step-review-commit"
        >
          {state.kind === 'submitting' ? 'Committing…' : 'Commit tenant'}
        </button>
      </div>
    </div>
  );
}

function renderSummary(spec: ProvisioningSpec): JSX.Element {
  const moduleCount = 3 + spec.modules.requestedSlugs.length;
  return (
    <section
      className="ferro-field"
      aria-labelledby="step-review-summary-label"
      data-testid="step-review-summary"
    >
      <h2 id="step-review-summary-label" className="ferro-field__label">
        Tenant summary
      </h2>
      <dl className="ferro-wizard__summary">
        <div>
          <dt>Slug</dt>
          <dd data-testid="step-review-slug">{spec.basic.slug}</dd>
        </div>
        <div>
          <dt>Display name</dt>
          <dd>{spec.basic.displayName}</dd>
        </div>
        <div>
          <dt>Legal entity</dt>
          <dd>{spec.basic.legalName}</dd>
        </div>
        <div>
          <dt>Vertical</dt>
          <dd>{spec.basic.vertical}</dd>
        </div>
        <div>
          <dt>Customer host</dt>
          <dd>
            <code>
              {spec.domains.find((d) => d.surface === 'customer')?.hostname}
            </code>
          </dd>
        </div>
        <div>
          <dt>Admin host</dt>
          <dd>
            <code>{spec.domains.find((d) => d.surface === 'admin')?.hostname}</code>
          </dd>
        </div>
        <div>
          <dt>Timezone</dt>
          <dd>{spec.config.timezone}</dd>
        </div>
        <div>
          <dt>Services</dt>
          <dd>{spec.config.serviceCatalog.length} configured</dd>
        </div>
        <div>
          <dt>Modules</dt>
          <dd data-testid="step-review-modules">
            {moduleCount} total ({3} base + {spec.modules.requestedSlugs.length} selected)
          </dd>
        </div>
        <div>
          <dt>Owner</dt>
          <dd>
            {spec.owner.firstName} {spec.owner.lastName} — {spec.owner.email}
          </dd>
        </div>
      </dl>
    </section>
  );
}

function renderIssues(
  issues: ReadonlyArray<AssemblyIssue>,
  onGoToStep?: (step: TenantWizardStep) => void,
): JSX.Element {
  // Group by step so the admin can fix one screen at a time.
  const byStep = new Map<TenantWizardStep, AssemblyIssue[]>();
  for (const issue of issues) {
    const bucket = byStep.get(issue.step) ?? [];
    bucket.push(issue);
    byStep.set(issue.step, bucket);
  }
  return (
    <section
      className="ferro-field"
      aria-labelledby="step-review-issues-label"
      data-testid="step-review-issues"
    >
      <h2 id="step-review-issues-label" className="ferro-field__label">
        Fix before committing
      </h2>
      <p role="alert" className="ferro-form__error">
        {issues.length} field{issues.length === 1 ? '' : 's'} need
        {issues.length === 1 ? 's' : ''} attention before commit.
      </p>
      <ul className="ferro-wizard__issues">
        {Array.from(byStep.entries()).map(([step, stepIssues]) => (
          <li key={step} data-testid={`step-review-issue-group-${step}`}>
            <strong>{TENANT_WIZARD_STEP_LABELS[step]}</strong>
            {onGoToStep ? (
              <>
                {' — '}
                <button
                  type="button"
                  className="ferro-btn ferro-btn--ghost"
                  onClick={() => onGoToStep(step)}
                  data-testid={`step-review-jump-${step}`}
                >
                  Fix
                </button>
              </>
            ) : null}
            <ul>
              {stepIssues.map((issue) => (
                <li key={`${issue.path}:${issue.message}`}>
                  <code>{issue.path}</code> — {issue.message}
                </li>
              ))}
            </ul>
          </li>
        ))}
      </ul>
    </section>
  );
}

function renderCommitState(state: StepReviewCommitState): JSX.Element | null {
  if (state.kind === 'idle') return null;
  if (state.kind === 'submitting') {
    return (
      <p
        className="ferro-muted"
        role="status"
        aria-live="polite"
        data-testid="step-review-submitting"
      >
        Committing tenant…
      </p>
    );
  }
  if (state.kind === 'created') {
    return (
      <section
        className="ferro-form__success"
        role="status"
        aria-live="polite"
        data-testid="step-review-created"
      >
        <h3>Tenant provisioned</h3>
        <p>
          <code>{state.response.tenant.slug}</code> is ready. Owner invite (
          {state.response.ownerEmail}) will be queued by the invite flow.
        </p>
        <p className="ferro-muted">
          {state.response.modules.newlyActivatedCount} module activation row(s)
          written. Status: <code>{state.response.tenant.status}</code>.
        </p>
      </section>
    );
  }
  if (state.kind === 'already_exists') {
    return (
      <section
        className="ferro-form__error"
        role="alert"
        data-testid="step-review-already-exists"
      >
        <h3>Tenant already exists</h3>
        <p>
          A tenant with slug <code>{state.response.tenant.slug}</code> is already
          provisioned. Nothing was changed. Pick a different slug in Step 1 or
          open the existing tenant in the admin console.
        </p>
      </section>
    );
  }
  return (
    <section
      className="ferro-form__error"
      role="alert"
      data-testid="step-review-error"
    >
      <h3>Commit failed</h3>
      <p>
        {state.message}
        {state.status > 0 ? ` (status ${state.status})` : ''}
      </p>
      {state.body?.issues ? (
        <ul>
          {state.body.issues.map((i) => (
            <li key={`${i.path}:${i.message}`}>
              <code>{i.path}</code> — {i.message}
            </li>
          ))}
        </ul>
      ) : null}
      {state.body?.hostname ? (
        <p>
          Conflicting hostname: <code>{state.body.hostname}</code>
        </p>
      ) : null}
    </section>
  );
}
